import { ApolloProvider } from "@apollo/client/react";
import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import React, {
  FC,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTextDomainContext } from "@/app/providers";
import { ErrorLink } from "@apollo/client/link/error";
import { useAuth } from "../useAuth";
import { useNavigate } from "react-router";
import { useSnackbar } from "notistack";
import { isAbortedRequestError, sendSlackError } from "@/utils";

const { VITE_API_URL_GRAPHQL, VITE_NODE_ENV } = import.meta.env;

interface GraphQLProviderProps {
  children: ReactNode;
}

interface GraphQLErrorItem {
  message?: string;
}

export const GraphQLProvider: FC<GraphQLProviderProps> = ({ children }) => {
  const { token } = useAuth();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const gettextRef = useRef(gettext);
  gettextRef.current = gettext;
  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  enqueueSnackbarRef.current = enqueueSnackbar;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const [client, setClient] = useState<ApolloClient | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const setupClient = async (): Promise<void> => {
      try {
        const cache = new InMemoryCache();
        // const indexedDBWrapper = new IndexedDBWrapper('apollo-persist-cache');
        // const indexedDBWrapper = new IndexedDBWrapper("cache");

        // await persistCache({
        //   cache,
        //   storage: indexedDBWrapper,
        // });

        const authMiddleware = new ApolloLink((operation, forward) => {
          if (token) {
            operation.setContext({
              headers: {
                authorization: `Bearer ${token}`,
              },
            });
          }
          return forward(operation);
        });

        // Custom fetch that strips the abort signal to prevent in-flight queries from
        // throwing "The operation was aborted." DOMException when navigating between pages.
        const customFetch: typeof fetch = (uri, options) => {
          if (options && options.signal) {
            const { signal: _signal, ...rest } = options;
            return fetch(uri, rest);
          }
          return fetch(uri, options);
        };

        const httpLink = new HttpLink({
          uri: VITE_API_URL_GRAPHQL,
          fetch: customFetch,
        });

        const handleErrors = ({ graphQLErrors, networkError }: any): void => {
          if (graphQLErrors) {
            graphQLErrors.forEach((item: GraphQLErrorItem) => {
              const { message } = item;
              if (!message) return;

              switch (message.toString()) {
                case "Your session has expired. Please log in again.":
                case "Invalid authorization token. Please provide a valid token.":
                  enqueueSnackbarRef.current(
                    gettextRef.current(
                      "Your login session has been expired. Please log in again",
                    ),
                    { variant: "info" },
                  );
                  navigateRef.current("/logout", { replace: true });
                  break;

                case "Signature verification failed":
                case "Not enough segments":
                  enqueueSnackbarRef.current(
                    gettextRef.current(
                      "A suspicious activity was detected. For security reasons, you must log in again",
                    ),
                    { variant: "warning" },
                  );
                  navigateRef.current("/logout", { replace: true });
                  break;

                case "Authorization token is missing. Please provide a valid token.":
                  // Skip showing this error
                  break;

                default:
                  if (VITE_NODE_ENV !== "production") {
                    enqueueSnackbarRef.current("Backend Error: " + message, {
                      variant: "error",
                    });
                    sendSlackError("Backend Error: " + message);
                  }
                  break;
              }
            });
          }
          if (networkError) {
            // Ignore aborted/cancelled requests to prevent noisy user-facing errors.
            if (isAbortedRequestError(networkError)) {
              return;
            }
            enqueueSnackbarRef.current(
              "Network error: " + networkError.message,
              {
                variant: "error",
              },
            );
          }
        };

        const errorLink = new ErrorLink(handleErrors);

        const newClient = new ApolloClient({
          link: from([authMiddleware, errorLink, httpLink]),
          cache,
          // connectToDevTools: true,
          defaultOptions: {
            watchQuery: {
              fetchPolicy: "cache-and-network",
              errorPolicy: "all" as any,
            },
            query: {
              fetchPolicy: "cache-first",
              errorPolicy: "all" as any,
            },
            mutate: {
              errorPolicy: "all" as any,
            },
          } as any,
        });

        setClient(newClient);
      } catch (error) {
        // console.error("Error setting up Apollo Client:", error);
        const errorMessage =
          error instanceof Error ? error.toString() : "Unknown error";
        enqueueSnackbarRef.current(
          "Error setting up GraphQL client " + errorMessage,
          {
            variant: "error",
          },
        );
      } finally {
        setLoading(false);
      }
    };

    setupClient();
  }, [token]);

  if (loading) {
    return <div>Loading...</div>; // Display a loading state while the client is being set up
  }

  if (!client) {
    return <div>Error initializing GraphQL client</div>; // Handle the case where the client fails to initialize
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
